import styled from "styled-components";
import AntTable, { TableProps } from "antd/es/table";
import { COLLAPSED_DESKTOP_MENU, MOBILE_SEARCH } from "common/constants";
import { paginationStyles } from "common/Pagination/Pagination";
import { hexToRGB } from "@shared/utils/misc";
import localStorageService from "utils/localStorageService";
import { useEffect, useState } from "react";
import Text from "common/Typography/Text";
import useMedia from "utils/hooks/useMedia";

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];
const DEFAULT_PAGE_SIZE_KEY = "default_table_page_size";

type StyledTableProps<RecordType extends object = any> = {
  itemsType?: string;
  onPaginationChange?: (pageNumber: number, pageSize: number) => void;
  shouldInitializePageNumber?: boolean;
} & TableProps<RecordType>;

const Table = <RecordType extends object = any>({
  dataSource,
  itemsType,
  onPaginationChange,
  shouldInitializePageNumber,
  ...props
}: StyledTableProps<RecordType>) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);

  const getDefaultPageSize = () => {
    const pageSize = parseInt(
      localStorageService.getValue(DEFAULT_PAGE_SIZE_KEY) || ""
    );
    const foundPageSize = PAGE_SIZE_OPTIONS.find(
      (option) => option === pageSize
    );
    return foundPageSize ? pageSize : DEFAULT_PAGE_SIZE;
  };
  const [pageSize, setPageSize] = useState(getDefaultPageSize);
  const [pageNumber, setPageNumber] = useState(1);

  const hidePagination = !dataSource || dataSource.length < DEFAULT_PAGE_SIZE;
  const additionalProps = {} as Pick<TableProps<RecordType>, "pagination">;

  useEffect(() => {
    if (shouldInitializePageNumber) {
      setPageNumber(1);
    }
  }, [shouldInitializePageNumber]);

  useEffect(() => {
    onPaginationChange?.(pageNumber, pageSize);
  }, [pageNumber, pageSize]);

  if (hidePagination) {
    additionalProps.pagination = false;
  } else if (dataSource) {
    additionalProps.pagination = {
      pageSizeOptions: PAGE_SIZE_OPTIONS.map((option) => option.toString()),
      showSizeChanger: dataSource.length > Math.min(...PAGE_SIZE_OPTIONS),
      pageSize,
      size: isMobile ? "small" : "middle",
      current: pageNumber,
    };
  }

  const originalOnChange = props.onChange;
  props.onChange = (pagination, ...otherArgs) => {
    if (pagination.pageSize) {
      localStorageService.setValue(DEFAULT_PAGE_SIZE_KEY, pagination.pageSize);
      setPageSize(pagination.pageSize);
    }
    if (originalOnChange) originalOnChange(pagination, ...otherArgs);
    if (pagination.current) setPageNumber(pagination.current);
  };

  const showNumOfItemsInTable =
    itemsType && !!dataSource?.length && !hidePagination;

  return (
    <div style={{ overflow: isMobile ? "hidden" : "initial" }}>
      <StyledTable {...additionalProps} {...props} dataSource={dataSource} />
      {showNumOfItemsInTable && (
        <NumOfItems $isMobile={isMobile}>
          {dataSource?.length} {itemsType}
        </NumOfItems>
      )}
    </div>
  );
};

export default Table;

const StyledTable = styled(AntTable)<any>`
  .ant-table {
    overflow: auto hidden;
    table {
      border-color: ${(p) => p.theme.colors.lightestBorderGrey};
    }
    td.ant-table-cell-row-hover {
      background: ${(p) => p.theme.colors.cardBgColor};
    }
  }
  .ant-table-thead th.ant-table-column-sort,
  .ant-table-thead:hover > tr > th:hover {
    background: ${(p) => p.theme.colors.selectedTableColumnBgColor};
  }
  .ant-table-column-title,
  .ant-table-thead > tr > th {
    color: ${(p) => p.theme.colors.titleColor};
  }
  .ant-table-cell {
    color: ${(p) => p.theme.colors.textColor};
  }
  .ant-table-column-sorter-up.active,
  .ant-table-column-sorter-down.active {
    color: ${(p) => p.theme.colors.primaryBlue}!important;
  }
  .ant-table,
  .ant-table-tbody > tr.ant-table-placeholder:hover > td {
    background: ${(p) => p.theme.colors.cardBgColor};
  }
  .ant-empty-description {
    color: ${(p) => p.theme.colors.secondaryGrey};
  }

  .ant-empty-img-simple-ellipse,
  .ant-empty-img-simple-path {
    fill: ${(p) => p.theme.colors.emptyColor};
  }
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    border-color: ${(p) => p.theme.colors.lightestBorderGrey};
  }

  .ant-spin-container.ant-spin-blur {
    background: ${(p) => p.theme.colors.cardBgColor};
    opacity: 0.8;
    &::after {
      background: ${(p) => p.theme.colors.cardBgColor};
      opacity: 0.8;
    }
  }
  .ant-spin-dot-item {
    background: ${(p) => p.theme.colors.primaryBlue};
  }

  .ant-spin-container::after {
    transition: none;
  }
  tr.ant-table-expanded-row > td,
  tr.ant-table-row-selected > td {
    background: ${(p) => p.theme.colors.lightBackground};
  }
  td.ant-table-column-sort,
  .ant-table-tbody > tr.ant-table-row:hover > td,
  .ant-table-thead > tr > th {
    background: ${(p) => p.theme.colors.tableHoverBgColor} !important;
  }
  th {
    user-select: none;
  }
  .ant-table-row-expand-icon {
    background: ${(p) => p.theme.colors.cardBgColor};
    border-color: ${(p) => p.theme.colors.lightestBorderGrey};
  }
  .ant-table-row-expand-icon:focus,
  .ant-table-row-expand-icon:hover {
    color: ${(p) => p.theme.colors.primaryBlue};
  }

  .ant-pagination.mini .ant-pagination-item,
  .ant-pagination.mini .ant-pagination-prev {
    margin-right: 4px;
  }

  .ant-table-tbody > tr.ant-table-placeholder {
    background-color: ${(p) => p.theme.colors.cardBgColor};
  }
  .ant-table-thead > tr > td {
    background: ${(p) => p.theme.colors.tableHoverBgColor};
    border-color: ${(p) => p.theme.colors.lightestBorderGrey};
  }

  .ant-table-thead
    > tr
    > th:not(:last-child):not(.ant-table-selection-column):not(
      .ant-table-row-expand-icon-cell
    ):not([colspan])::before {
    background-color: rgba(0, 0, 0, 0.06);
  }
  .ant-table-column-sorter {
    color: #bfbfbf;
  }
  .ant-table-column-sorters:hover .ant-table-column-sorter {
    color: #a6a6a6;
  }

  .ant-table-tbody .ant-table-row > .ant-table-cell-row-hover {
    background-color: ${(p) => p.theme.colors.tableHoverBgColor} !important;
  }

  .ant-table-tbody
    .ant-table-row.ant-table-row-selected:not(:hover)
    > .ant-table-cell:not(.ant-table-column-sort) {
    background: ${(p) => p.theme.colors.lightBackground} !important;
  }

  @media (max-width: ${COLLAPSED_DESKTOP_MENU}) {
    .ant-table-column-sorters {
      justify-content: unset;
      .ant-table-column-title {
        margin-right: 8px;
        flex: unset;
      }
    }
  }

  .ant-table-cell.ant-table-selection-column {
    min-width: 50px;
  }

  ${paginationStyles};
  ${(p) =>
    p.theme.isNewLook &&
    `
    /* Column headers use the micro role on a hairline, with no tinted
       header row (the same as raw antd tables in styles/newLook.scss). */
    .ant-table-thead > tr > th,
    .ant-table-thead > tr > td {
      font-size: 11px;
      line-height: 14px;
      font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: ${p.theme.colors.mutedText};
      background: transparent !important;
      border-bottom: 1px solid ${p.theme.colors.cardBorderColor};
    }
    .ant-table-column-title {
      color: inherit;
    }
    .ant-table-tbody > tr > td {
      border-bottom-color: ${p.theme.colors.cardBorderColor};
      font-variant-numeric: tabular-nums;
    }
    /* The card edge closes the table; no hairline right above it. */
    .ant-table-tbody > tr:last-child > td {
      border-bottom-color: transparent;
    }
    .ant-table-thead > tr > th::before {
      background-color: transparent !important;
    }
    /* The sorted column is shown by its header arrow only, not a tinted
       column; rows keep their hover and selected tints. */
    .ant-table-tbody > tr > td.ant-table-column-sort {
      background: ${p.theme.colors.cardBgColor} !important;
    }
    .ant-table-tbody > tr.ant-table-row:hover > td.ant-table-column-sort,
    .ant-table-tbody .ant-table-row > td.ant-table-column-sort.ant-table-cell-row-hover {
      background: ${p.theme.colors.tableHoverBgColor} !important;
    }
    .ant-table-tbody > tr.ant-table-row-selected > td.ant-table-column-sort {
      background: ${p.theme.colors.lightBackground} !important;
    }
    .ant-table-column-sorter {
      color: ${hexToRGB(p.theme.colors.secondaryText, 0.45)};
    }
  `}
`;

const NumOfItems = styled(Text)<{ $isMobile: boolean }>`
  color: ${(p) => p.theme.colors.secondaryGrey};
  ${(p) =>
    p.$isMobile &&
    `float: right;
  `};
  ${(p) =>
    !p.$isMobile &&
    `position: absolute;
     transform: translate(0, -42px);
  `};
`;
